/**
 * useLiveChatAttachments — les pièces jointes du chat du live, dans le fil du tuteur.
 *
 * Les gestes de `CommonChat` en live, avec les pièces déjà portées pour la messagerie de l'élève :
 *  - joindre une photo de l'appareil (la route legacy `camera`, `formId: "chat"`, la photo reprise au
 *    retour), de la galerie (mobile seulement), un document pdf ou image, ou un vocal (l'enregistreur
 *    de la messagerie, `ConversationVoiceRecorder`) — les formulaires de `conversationFiles` ;
 *  - dans « Tous », la pièce part dans le fil du tuteur de la séance, comme l'ancien chat, qui y
 *    basculait (`showGroupChatConfirm` → `goToMyTutor`) : le chat passe sur ce fil ; sur la puce d'un
 *    autre tuteur, elle part dans son fil ;
 *  - toucher une photo ou un document : la visionneuse (`viewerFor`), sans quitter le live ;
 *  - toucher un vocal : il joue, ou s'arrête (`useConversationVoicePlayer`) ; chargé, il se règle,
 *    son curseur et sa vitesse, comme dans la messagerie ;
 *  - toucher un lien du texte : une adresse web ou un e-mail s'ouvre, un numéro propose
 *    « Appeler » ou « Envoyer un SMS » (`conversationLinks`, la feuille de la messagerie), comme
 *    `CommonChat`, qui rendait les liens touchables.
 * L'envoi est au moteur (`useLiveChat.sendAttachment`, `sendVoice`) : la messagerie, puis l'annonce
 * au tuteur ; un vocal passe par la file persistée des vocaux de la messagerie. Chaque pièce part dans
 * le fil où son geste a commencé (l'appareil photo, la galerie, le document, l'enregistreur) : un
 * toucher de notification pendant le geste change le fil ouvert, pas sa destination.
 */
import { useIsFocused, useNavigation } from "@react-navigation/native"
import * as DocumentPicker from "expo-document-picker"
import * as ImagePicker from "expo-image-picker"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { clearLastPhoto } from "../../../../../../data/redux/actions/camera"
import { type ComposerAttachSource } from "../../../../../../design-system/student"
import { GROUP_CHANNEL, type LiveChatChannel } from "../../../../../../engine/live/student/liveChatChannels"
import { type LiveChatController } from "../../../../../../engine/live/student/useLiveChat"
import { type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import { type UIConversationMessage, type UIMessageLink } from "../../../types/studentConversation"
import { linkHref, phoneHref, type PhoneAction } from "../Messaging/conversationLinks"
import { attachmentForm, documentPart, imagePart, isPdfFile, uploadPart } from "../Messaging/conversationFiles"
import { messengerVoiceFileName } from "../Messaging/messengerVoiceOutbox"
import { openConversationLink } from "../Messaging/openConversationLink"
import { useConversationVoicePlayer } from "../Messaging/useConversationVoicePlayer"
import {
    CONVERSATION_ATTACH_SOURCES,
    viewerFor,
    type ConversationViewer,
} from "../Messaging/useStudentTutorConversationState"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

/** Le `formId` que l'appareil photo legacy pose sur une photo du chat, celui de la messagerie. */
const CAMERA_FORM_ID = "chat"

type Chat = Pick<LiveChatController, "threadId" | "sendAttachment" | "sendVoice"> & {
    channel: LiveChatChannel
    /** `null` : le fil du tuteur de la séance. */
    setChannel: (channel: LiveChatChannel | null) => void
}

type RootState = {
    data?: { currentUser?: { object?: { display_name?: string | null } | null } }
    camera?: { lastPhoto?: { uri?: string } | null; formId?: string | null }
}

export function useLiveChatAttachments(chat: Chat, messages: UIConversationMessage[]) {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as { navigate: (route: string, params?: object) => void }
    const isFocused = useIsFocused()
    const displayName = useSelector((state: RootState) => selectDisplayedStudent(state)?.display_name)
    const lastPhoto = useSelector((state: RootState) => state.camera?.lastPhoto)
    const lastPhotoFormId = useSelector((state: RootState) => state.camera?.formId)
    const [viewer, setViewer] = useState<ConversationViewer | null>(null)
    // Le fil de l'enregistreur ouvert (`undefined` : fermé), figé à son ouverture.
    const [recorderThread, setRecorderThread] = useState<string | null | undefined>(undefined)
    // Le fil où le détour par l'appareil photo a commencé.
    const cameraThreadRef = useRef<string | null>(null)
    const [pendingSource, setPendingSource] = useState<ComposerAttachSource | null>(null)
    const [phone, setPhone] = useState<string | null>(null)
    const voice = useConversationVoicePlayer(chat.threadId)
    const { sendAttachment, sendVoice } = chat

    const sendImage = useCallback(
        async (uri: string, thread: string | null) => {
            const part = await uploadPart(imagePart(uri, displayName))
            if (part != null) sendAttachment("image", attachmentForm("image", part), { uri }, thread)
        },
        [displayName, sendAttachment]
    )

    // La photo de l'appareil revient par le magasin ; seul le live au premier plan la prend.
    useEffect(() => {
        if (!isFocused || chat.threadId == null || lastPhotoFormId !== CAMERA_FORM_ID || lastPhoto?.uri == null) return
        const uri = lastPhoto.uri
        dispatch(clearLastPhoto() as never)
        void sendImage(uri, cameraThreadRef.current ?? chat.threadId)
        cameraThreadRef.current = null
    }, [dispatch, isFocused, chat.threadId, lastPhoto, lastPhotoFormId, sendImage])

    const run = useCallback(
        async (source: ComposerAttachSource) => {
            const thread = chat.threadId
            if (source === "voice") {
                voice.stop()
                setRecorderThread(thread)
                return
            }
            if (source === "camera") {
                cameraThreadRef.current = thread
                navigation.navigate("camera", { formId: CAMERA_FORM_ID })
                return
            }
            if (source === "gallery") {
                const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ["images"], quality: 1 })
                const asset = result.canceled ? null : result.assets?.[0]
                if (asset?.uri) await sendImage(asset.uri, thread)
                return
            }
            const result = await DocumentPicker.getDocumentAsync({ type: ["application/pdf", "image/*"] })
            const asset = result.canceled ? null : result.assets?.[0]
            if (asset?.uri == null) return
            // Une image choisie comme document part comme une photo (`pickFile` → `onSendImage`).
            if (!isPdfFile(asset)) {
                await sendImage(asset.uri, thread)
                return
            }
            const part = documentPart(asset)
            const uploadable = await uploadPart(part)
            if (uploadable != null) {
                sendAttachment(
                    "document",
                    attachmentForm("document", uploadable),
                    { uri: asset.uri, name: part.name },
                    thread
                )
            }
        },
        [chat.threadId, navigation, sendImage, sendAttachment, voice]
    )

    // Dans « Tous », le chat passe d'abord sur le fil du tuteur ; le geste part une fois le fil changé.
    useEffect(() => {
        if (pendingSource == null || chat.channel === GROUP_CHANNEL || chat.threadId == null) return
        setPendingSource(null)
        void run(pendingSource)
    }, [pendingSource, chat.channel, chat.threadId, run])

    const attach = useCallback(
        (source: ComposerAttachSource) => {
            if (chat.channel === GROUP_CHANNEL) {
                chat.setChannel(null)
                setPendingSource(source)
                return
            }
            void run(source)
        },
        [chat, run]
    )

    const sendRecording = useCallback(
        (file: VoiceFile) =>
            sendVoice(file, messengerVoiceFileName(displayName, file.extension), recorderThread ?? chat.threadId),
        [displayName, sendVoice, recorderThread, chat.threadId]
    )

    const onPressLink = useCallback((link: UIMessageLink) => {
        const href = linkHref(link)
        if (href != null) openConversationLink(href, link.kind)
        else setPhone(link.value)
    }, [])

    return {
        attachSources: chat.threadId != null ? CONVERSATION_ATTACH_SOURCES : [],
        onAttach: attach,
        onOpenAttachment: (message: UIConversationMessage) => {
            const next = viewerFor(message, messages)
            if (next != null) setViewer(next)
        },
        voicePlayback: voice.playback,
        onToggleVoice: voice.toggle,
        onSeekVoice: voice.seek,
        onCycleVoiceRate: voice.cycleRate,
        onPressLink,
        /** Le numéro touché ; `null` : la feuille « Appeler / Envoyer un SMS » est fermée. */
        phone,
        onChoosePhone: (action: PhoneAction) => {
            if (phone != null) openConversationLink(phoneHref(phone, action), "phone")
        },
        onClosePhone: () => setPhone(null),
        viewer,
        closeViewer: () => setViewer(null),
        recorder:
            recorderThread !== undefined
                ? {
                      threadId: recorderThread,
                      onClose: () => setRecorderThread(undefined),
                      onSend: sendRecording,
                  }
                : null,
    }
}
